import { addDays, areIntervalsOverlapping, differenceInYears, format, isWithinInterval, parseISO } from 'date-fns';
import { balances, regularTimeTaken, timeOffEntries } from '../data/timeOff';
import type { Employee, Policy, PtoBalance, TimeOffHistoryRow } from '../types/hr';
import { TODAY } from './format';
import { getHRState } from './hrStore';
import { tierForYears } from './policy';

interface DatedRange {
  start: string;
  end: string;
}

const TODAY_ISO = format(TODAY, 'yyyy-MM-dd');

export function getPolicy(id?: string): Policy | undefined {
  return getHRState().policies.find((p) => p.id === id);
}

export function policyName(id?: string): string {
  return getPolicy(id)?.name ?? 'No policy';
}

export function isOutOn(range: DatedRange, date: Date): boolean {
  return isWithinInterval(date, { start: parseISO(range.start), end: parseISO(range.end) });
}

export function overlaps(range: DatedRange, start: Date, end: Date): boolean {
  return areIntervalsOverlapping({ start: parseISO(range.start), end: parseISO(range.end) }, { start, end }, { inclusive: true });
}

export function balanceFor(employeeId: string): PtoBalance | undefined {
  return balances.find((b) => b.employeeId === employeeId);
}

export function accrualStart(hireIso: string): Date {
  return addDays(parseISO(hireIso), 30);
}

/** Future approved days: scheduled time off plus requests approved this session. */
export function scheduledDays(employeeId: string): number {
  const fromEntries = timeOffEntries.filter((e) => e.employeeId === employeeId && e.start > TODAY_ISO).reduce((sum, e) => sum + e.days, 0);
  const fromRequests = getHRState()
    .requests.filter((r) => r.employeeId === employeeId && r.status === 'approved')
    .reduce((sum, r) => sum + r.days, 0);
  return fromEntries + fromRequests;
}

export function historyFor(employee: Employee): TimeOffHistoryRow[] {
  if (employee.policyId === 'regular') {
    return regularHistory(employee);
  }
  if (employee.policyId === 'newhire') {
    const year = parseISO(employee.hireDate).getFullYear();
    return [
      {
        id: `${employee.id}-nh`,
        date: employee.hireDate,
        description: `Assigned to New Hire PTO — accrual begins ${format(accrualStart(employee.hireDate), 'MMM d, yyyy')}`,
        balance: 0,
        year,
      },
    ];
  }
  return [];
}

/** Completed years of service on a given date. */
export function serviceYears(employee: Employee, on: Date = TODAY): number {
  return Math.max(0, differenceInYears(on, parseISO(employee.hireDate)));
}

/**
 * Regular PTO ledger built from the policy's milestone schedule: Dec 31 carryover limit,
 * the Jan 1 grant for the employee's tier, then time taken — reconciling to today's balance.
 */
function regularHistory(employee: Employee): TimeOffHistoryRow[] {
  const schedule = getPolicy('regular')?.form.schedule ?? [];
  const postDate = new Date(2026, 0, 1);
  const tier = tierForYears(schedule, serviceYears(employee, postDate));
  const grant = Number(tier?.days) || 0;
  const carry = tier?.carryoverEnabled ? Number(tier.carryover) || 0 : 0;
  const available = balanceFor(employee.id)?.available ?? 0;
  const totalUsed = Math.max(0, carry + grant - available);
  const yrs = Number(tier?.afterYears) || 0;

  const smallTotal = regularTimeTaken.reduce((s, t) => s + t.days, 0);
  const taken = totalUsed >= smallTotal ? regularTimeTaken : [];
  const summer = totalUsed - (taken.length ? smallTotal : 0);

  type Step = Omit<TimeOffHistoryRow, 'balance' | 'year' | 'id'>;
  const y2025: Step[] = [
    { date: '2025-11-26', description: 'Time off — Thanksgiving', used: 1 },
    { date: '2025-12-22', description: 'Time off — Holiday (Dec 22–24)', used: 3 },
    { date: '2025-12-31', description: carry ? `Year-end — days above the ${carry}-day carryover limit forfeited` : 'Year-end — unused balance forfeited (use it or lose it)', used: 2 },
  ];
  const y2026: Step[] = [
    { date: '2026-01-01', description: `Carried over from 2025 (limit ${carry} days)` },
    {
      date: '2026-01-01',
      description: `Yearly accrual — ${grant} days (${yrs === 0 ? 'starting schedule' : `${yrs}+ ${yrs === 1 ? 'year' : 'years'} of service`})`,
      earned: grant,
    },
    ...taken.slice(0, 2).map((t) => ({ date: t.date, description: t.description, used: t.days })),
    ...(summer > 0 ? [{ date: '2026-07-06', description: `Time off — Summer vacation (${summer} ${summer === 1 ? 'day' : 'days'})`, used: summer }] : []),
    ...taken.slice(2).map((t) => ({ date: t.date, description: t.description, used: t.days })),
  ];

  let balance = carry + 6;
  const rows: TimeOffHistoryRow[] = [];
  [...y2025, ...y2026].forEach((s, i) => {
    balance = balance - (s.used ?? 0) + (s.earned ?? 0);
    rows.push({ ...s, id: `${employee.id}-rh${i}`, balance, year: parseISO(s.date).getFullYear() });
  });
  return rows.reverse();
}

export function usedYtd(employee: Employee): number {
  return historyFor(employee)
    .filter((h) => h.year === TODAY.getFullYear())
    .reduce((sum, h) => sum + (h.used ?? 0), 0);
}
